import axiosIns from '@axios'

/**
 * RFILE-590: файлы РЕЗУЛЬТАТА агента в панели (ТЗ «Файлы результата агента» §8 пп. 2–4).
 *
 * ОТКУДА ФАЙЛЫ. Платформа кладёт в сообщение чата два поля — `files` (принятые, с номером) и
 * `files_failed` (не попавшие, с причиной). Кнопка рисуется только по `files`: у файла без номера
 * скачивать нечего, а ссылку в тексте платформа для таких файлов больше не дописывает (RFILE-587).
 *
 * ЧЕМ ОТЛИЧАЕТСЯ ОТ ВЛОЖЕНИЙ (`agentAttachments.js`). Вложение человек ОТПРАВЛЯЕТ агенту и качает
 * его раннер; файл результата человек ПОЛУЧАЕТ и качает сам — методом `agentresult`, который
 * открывает только файлы с происхождением `agent_result` этого же пользователя (RFILE-586).
 *
 * ЗНАЧЕНИЯ ИЗ КАДРА — НЕДОВЕРЕННЫЕ. Имя файла пишет агент. Поэтому имена здесь только разбираются
 * и нигде не превращаются в разметку: в шаблоне они выводятся интерполяцией, а не `v-html`.
 */

/** Принятые файлы из кадра: только с числовым номером и непустым именем. */
export function resultFilesOf(list) {
  if (!Array.isArray(list)) return []
  return list
    .map(f => ({
      id: Number(f && f.id),
      name: String((f && f.name) || '').trim(),
      size: Number(f && f.size) || 0,
    }))
    .filter(f => Number.isInteger(f.id) && f.id > 0 && f.name)
}

/**
 * RFILE-613: места «принимается» (ТЗ «Фоновая приёмка» §8 п. 1). При фоновой приёмке ответ приходит
 * раньше, чем файл проверен, и место в `files` ещё без номера. Кнопки у такого места нет — скачивать
 * нечего, — но строка «принимается» говорит человеку, что файл будет. Место с номером — не сюда, а в
 * `resultFilesOf`; место без имени не показывается вовсе.
 */
export function pendingFilesOf(list) {
  if (!Array.isArray(list)) return []
  return list
    .filter(f => {
      const id = Number(f && f.id)
      return !(Number.isInteger(id) && id > 0)
    })
    .map(f => ({
      name: String((f && f.name) || '').trim(),
      size: Number(f && f.size) || 0,
    }))
    .filter(f => f.name)
}

/** Отказы из кадра: имя обязательно, причина — какая пришла. */
export function failedFilesOf(list) {
  if (!Array.isArray(list)) return []
  return list
    .map(f => ({
      name: String((f && f.name) || '').trim(),
      reason: String((f && f.reason) || '').trim(),
    }))
    .filter(f => f.name)
}

/**
 * RFILE-632 (ТЗ «Мост: файлы результата» §7 п. 3): состояние мест по заданиям — после переподключения сокета.
 *
 * Кадры `files`, пришедшие, пока сокет был закрыт, потеряны: пузырь остался бы «принимается» навсегда.
 * Ответ — в форме кадра (`jobid`, `files`, `files_failed`) и применяется тем же кодом. Отказ — пустой
 * список: переспросить можно при следующем подключении, а пугать человека сбоем служебного запроса незачем.
 */
export async function resultSlotsOf(jobIds) {
  if (!Array.isArray(jobIds) || jobIds.length === 0) return []
  try {
    const res = await axiosIns.post('/api/agent/result-slots', { job_ids: jobIds.slice(0, 50) })
    const jobs = res && res.data && res.data.jobs
    return Array.isArray(jobs) ? jobs : []
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('result-slots: состояние мест не получено —', (e && e.message) || e)
    return []
  }
}

/** Отдать браузеру скачивание по адресу. Освобождение не сразу: Safari начинает качать позже клика. */
function clickDownload(href, name, revoke) {
  const a = document.createElement('a')
  a.href = href
  if (name) a.download = name
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  if (revoke) setTimeout(() => window.URL.revokeObjectURL(href), 10000)
}

/** Отказ платформы приходит JSON'ом, но `responseType: blob` делает его блобом — читаем текстом. */
async function jsonOfBlob(blob) {
  try {
    return JSON.parse(await blob.text()) || {}
  } catch (e) {
    return {}
  }
}

/**
 * Скачать файл результата. Бросает `Error` с причиной, которую можно показать человеку.
 *
 * ДВА ОТВЕТА ПЛАТФОРМЫ (§8 п. 4):
 *   — ТЕЛО. Кладём в `blob` и отдаём через `a[download]` с НАСТОЯЩИМ именем — как в
 *     `CbsPresentation.vue`. Страница файла в origin Cubus не открывается: HTML результата,
 *     показанный здесь, исполнялся бы с нашими cookie.
 *   — ССЫЛКА `{s3url}`. Уходим по ней ПЕРЕХОДОМ, а не `fetch` в блоб, как делает мост
 *     (`claudeBridge.js`). Мосту нужны сами байты, человеку — файл на диске; `fetch` к хранилищу
 *     упирается в его CORS, а переход — нет. Ссылка подписана с `Content-Disposition: attachment`
 *     (`S3Connector.presignedUrl`), поэтому браузер скачивает, а не открывает, и открывал бы
 *     всё равно в origin хранилища, не Cubus.
 */
export async function downloadAgentResult(file) {
  let res
  try {
    res = await axiosIns.post(
      '/api/download',
      { param: { method: 'agentresult', fileid: file.id } },
      { responseType: 'blob' },
    )
  } catch (e) {
    const data = e && e.response && e.response.data
    const body = data && typeof data.text === 'function' ? await jsonOfBlob(data) : {}
    throw new Error(body.error || (e && e.message) || 'не удалось скачать')
  }
  const data = res && res.data
  if (!data) throw new Error('пустой ответ платформы')
  const type = String(data.type || '').toLowerCase()
  if (type.indexOf('application/json') === 0) {
    const body = await jsonOfBlob(data)
    if (body.error) throw new Error(body.error)
    if (body.s3url) {
      clickDownload(body.s3url, file.name, false)
      return
    }
    // ⚠️ JSON без `error` и без `s3url` — это не «странный отказ», а, скорее всего, САМ ФАЙЛ:
    // файл результата бывает `.json`. Сегодня тело уходит с чужим типом (`sendBody` ставит
    // `application/ms-excel`), и до этой ветки файл не доходит, но стоит серверу начать отдавать
    // настоящий тип — и отчёт агента в JSON превратился бы здесь в ошибку «ни файла, ни ссылки».
  }
  clickDownload(window.URL.createObjectURL(data), file.name, true)
}
